/**
 * /earthworks/invoices — Ready to invoice (fieldiq-api#1300, EW-6): approved
 * field tickets on no live invoice, one panel per (customer, province / state,
 * currency) — the set one invoice may bill (GET /earthworks/invoices/billable).
 *
 * The invoices themselves list in FINANCE → Invoices, the one invoice list for
 * every vertical; this page is the queue and the "Create invoice" door. Every
 * ticket in a group starts ticked; the office unticks what waits for later.
 * A job that takes holdback is invoiced on its own (the API's
 * holdback_needs_one_job), and a group with such a job says so before Create.
 *
 * Create is admin / office staff. Group subtotals are the API's figures; a
 * ticked subset shows no recomputed sum.
 *
 * DOUBLE BILLING (Jason 2026-11-01: warn, don't refuse). A job here already
 * drawn from its quote says so in the panel (the group's `overlaps`, the
 * API's sentence) before Create; Create then answers 409 billing_overlap and
 * the panel offers "Confirm and invoice anyway", which resends with
 * confirm_overlap: true.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FilePlus2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useCreateEwInvoiceFromTickets, useEwBillable } from '../../../hooks/useEwInvoices';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { ewDay, ewRefusal, fmtMoney } from '../../../lib/earthworks';
import { canWriteEwInvoices, ewBillableSelectionProblem, ewBillingOverlapWarning } from '../../../lib/ew-invoices';
import type { EwBillableGroup } from '../../../types/ew-invoices';
import { EW_DATE_INPUT, EW_EYEBROW, EW_INPUT, EW_PRIMARY, EwEmpty, EwField, EwFormError, EwLoading, EwPanel } from '../EwBits';
import { EwOverlapConfirm, EwOverlapNote } from './EwOverlapConfirm';

export const EW_READY_TO_INVOICE_PATH = '/earthworks/invoices';

export const ewGroupKey = (g: EwBillableGroup) => `${g.customerId}|${g.provinceState ?? ''}|${g.currency ?? ''}`;

export function EwBillableGroupPanel({ group, canWrite, onCreated }: { group: EwBillableGroup; canWrite: boolean; onCreated: (id: string) => void }) {
  const create = useCreateEwInvoiceFromTickets();
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [overlap, setOverlap] = useState<string | null>(null);
  const picked = useMemo(() => group.tickets.filter((t) => !unticked.has(t.ticketId)), [group.tickets, unticked]);
  const problem = ewBillableSelectionProblem(picked);
  const hasHoldback = group.holdbackJobIds.length > 0;
  const multiJob = new Set(group.tickets.map((t) => t.jobId)).size > 1;

  const toggle = (id: string) => setUnticked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const pickedJobs = new Set(picked.map((t) => t.jobId));
  const notesForPicked = (group.overlaps ?? []).filter((o) => pickedJobs.has(o.jobId));

  const go = (confirm = false) => {
    setError(null);
    setOverlap(null);
    if (problem) return setError(problem);
    create.mutate({
      ticket_ids: picked.map((t) => t.ticketId),
      ...(dueDate ? { due_date: dueDate } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(confirm ? { confirm_overlap: true } : {}),
    }, {
      onSuccess: (inv) => {
        toast.success(inv?.invoiceNumber ? `${inv.invoiceNumber} drafted` : 'Invoice drafted');
        if (inv?.id) onCreated(inv.id);
      },
      onError: (err) => {
        const warning = ewBillingOverlapWarning(err);
        if (warning) setOverlap(warning);
        else setError(ewRefusal(err, 'The invoice could not be created.'));
      },
    });
  };

  return (
    <EwPanel
      eyebrow={`${group.provinceState || '—'} · ${group.currency || '—'}`}
      title={group.customerName || '—'}
      testId="ew-billable-group"
      footer={`${group.tickets.length} approved ticket${group.tickets.length === 1 ? '' : 's'} · ${fmtMoney(group.subtotal, group.currency)} before tax`}
    >
      {hasHoldback && multiJob && (
        <p role="note" className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text" data-testid="ew-billable-holdback-note">
          A job here takes holdback, which is tracked per job. Tick the tickets of one job for each invoice.
        </p>
      )}
      {notesForPicked.length > 0 && (
        <div className="space-y-1 border-b border-fieldiq-border px-4 py-3" data-testid="ew-billable-overlaps">
          {notesForPicked.map((o) => <EwOverlapNote key={o.jobId} text={o.note} />)}
        </div>
      )}
      <ul className="divide-y divide-fieldiq-border">
        {group.tickets.map((t) => (
          <li key={t.ticketId} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid="ew-billable-ticket">
            {canWrite && (
              <input
                type="checkbox"
                aria-label={`Include ${t.ticketNumber}`}
                checked={!unticked.has(t.ticketId)}
                onChange={() => toggle(t.ticketId)}
              />
            )}
            <Link to={`/earthworks/tickets/${t.ticketId}`} className="font-mono font-extrabold text-white underline underline-offset-2">{t.ticketNumber}</Link>
            <Link to={`/earthworks/jobs/${t.jobId}?tab=invoices`} className="font-mono text-fieldiq-text underline underline-offset-2">{t.jobNumber}</Link>
            <span className="text-fieldiq-text">{t.mode === 'per_day' ? ewDay(t.workDate) : 'Whole job'}</span>
            {t.holdbackEnabled && <span className="text-fieldiq-muted">Holdback job</span>}
            <span className="ml-auto font-mono text-white">{fmtMoney(t.subtotal, group.currency)}</span>
          </li>
        ))}
      </ul>
      {canWrite && (
        <div className="grid gap-3 border-t border-fieldiq-border px-4 py-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end">
          <EwField label="Due date (optional)" hint="Blank: the customer's payment terms.">
            <input type="date" className={EW_DATE_INPUT} aria-label="Invoice due date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </EwField>
          <EwField label="Notes (optional)">
            <input className={EW_INPUT} aria-label="Invoice notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </EwField>
          <button type="button" className={EW_PRIMARY} onClick={() => go()} disabled={create.isPending || !!problem} data-testid="ew-billable-create">
            <FilePlus2 className="size-4" aria-hidden="true" />
            {create.isPending ? 'Creating…' : `Create invoice (${picked.length})`}
          </button>
          {overlap && (
            <div className="sm:col-span-3">
              <EwOverlapConfirm message={overlap} pending={create.isPending} onConfirm={() => go(true)} onCancel={() => setOverlap(null)} />
            </div>
          )}
          {(error || problem) && <div className="sm:col-span-3"><EwFormError message={error || problem} /></div>}
        </div>
      )}
    </EwPanel>
  );
}

export default function EwReadyToInvoiceScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canWriteEwInvoices(user?.role);
  const query = useEwBillable();
  const groups = query.data ?? [];
  const failure = queryLoadFailure(query);

  return (
    <div data-testid="ew-ready-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={EW_EYEBROW}
        title="Ready to invoice"
        actions={
          <Link to="/finance/invoices?vertical=earthworks" className="inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10">
            Earthworks invoices
          </Link>
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <EwLoading label="Loading approved tickets" />
          ) : failure ? (
            <EwPanel eyebrow="Billing" title="Ready to invoice">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the approved tickets" />
            </EwPanel>
          ) : groups.length === 0 ? (
            <EwPanel eyebrow="Billing" title="Ready to invoice">
              <EwEmpty>No approved field tickets are waiting to be invoiced.</EwEmpty>
            </EwPanel>
          ) : (
            groups.map((g) => (
              <EwBillableGroupPanel key={ewGroupKey(g)} group={g} canWrite={canWrite} onCreated={(id) => navigate(`/earthworks/invoices/${id}`)} />
            ))
          )}
        </div>
      </section>
    </div>
  );
}
